import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_INVOICE_STATUSES,
  ALL_PAYMENT_STATUSES,
  type InvoiceView,
} from '@lims/shared-types';
import { clientsApi, invoicesApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  FilterBar,
  PageHeader,
  Panel,
  SearchField,
  SelectField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { RecordNumber, ViewTabs } from '../clients/record-layout.js';

/**
 * The invoice register.
 *
 * STATUS AND PAYMENT STATUS ARE SHOWN SEPARATELY, because they answer
 * different questions (criterion 5): `status` is what a person did to the
 * document (draft, issued, cancelled, discarded); `paymentStatus` is what has
 * been collected against it, derived by the backend and never recomputed
 * here. Approval never meant the invoice was paid, and being paid never
 * changes the document's own status.
 */
export function InvoicesPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  // A client file's invoices link arrives with `?clientId=`, so the register
  // opens already scoped to it rather than showing every laboratory client.
  const [clientId, setClientId] = useState<string | null>(
    searchParams.get('clientId'),
  );
  const [status, setStatus] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const clients = useQuery({
    queryKey: ['clients', 'selectable'],
    queryFn: () => clientsApi.list({ pageSize: 100, includeSuspended: false }),
  });

  const invoices = useQuery({
    queryKey: ['invoices', { page, search, clientId, status, paymentStatus }],
    queryFn: () =>
      invoicesApi.list({
        page,
        search,
        ...(clientId ? { clientId } : {}),
        ...(status ? { status } : {}),
        ...(paymentStatus ? { paymentStatus } : {}),
      }),
  });

  const columns: readonly DataColumn<InvoiceView>[] = [
    {
      key: 'number',
      header: t('invoices.number'),
      render: (invoice) => (
        // The number opens the invoice: the row's way in (D278). A draft has
        // no number yet and says so.
        <RecordNumber to={`/invoices/${invoice.id}`}>
          {invoice.invoiceNumber ?? t('status.invoice.draft')}
        </RecordNumber>
      ),
    },
    {
      key: 'client',
      header: t('invoices.client'),
      render: (invoice) => (
        <span className="text-default-800" dir="auto">
          {invoice.client.name ?? invoice.client.number}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('invoices.statusLabel'),
      render: (invoice) => (
        <StatusBadge
          label={t(`status.invoice.${invoice.status}`)}
          tone={
            invoice.status === 'issued'
              ? 'success'
              : invoice.status === 'cancelled' || invoice.status === 'discarded'
                ? 'warning'
                : 'neutral'
          }
        />
      ),
    },
    {
      key: 'paymentStatus',
      header: t('invoices.paymentStatusLabel'),
      render: (invoice) =>
        invoice.paymentStatus ? (
          <StatusBadge
            label={t(`status.payment.${invoice.paymentStatus}`)}
            tone={
              invoice.paymentStatus === 'paid'
                ? 'success'
                : invoice.paymentStatus === 'partially_paid'
                  ? 'warning'
                  : 'neutral'
            }
          />
        ) : (
          <span className="text-default-600">{t('common.notApplicable')}</span>
        ),
    },
    {
      key: 'dueDate',
      header: t('invoices.dueDate'),
      render: (invoice) => (
        <div className="flex items-center gap-inline">
          <span className="whitespace-nowrap text-default-700">
            {invoice.dueDate
              ? formatDate(invoice.dueDate, { locale })
              : t('common.notRecorded')}
          </span>
          {invoice.overdue ? (
            <StatusBadge size="sm" label={t('invoices.overdue')} tone="warning" />
          ) : null}
        </div>
      ),
    },
    {
      key: 'balance',
      header: t('invoices.balance'),
      align: 'end',
      render: (invoice) => (
        <span className="measure text-default-900" dir="ltr">
          {formatMoney(invoice.balanceMinor, {
            locale,
            currency: invoice.currency,
          })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (invoice) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/invoices/${invoice.id}`)}
        >
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('invoices.title')}
        subtitle={t('invoices.subtitle')}
        breadcrumbs={[{ label: t('nav.invoices') }]}
        actions={
          can(PERMISSIONS.INVOICES_CREATE) ? (
            <ActionButton priority="primary" onPress={() => navigate('/billing')}>
              {t('invoices.create')}
            </ActionButton>
          ) : null
        }
      />

      <ViewTabs
        label={t('invoices.statusLabel')}
        value={status}
        onChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
        options={[
          { value: '', label: t('common.all') },
          ...ALL_INVOICE_STATUSES.map((value) => ({
            value,
            label: t(`status.invoice.${value}`),
          })),
        ]}
      />

      <Panel>
        <FilterBar>
          <SearchField
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('invoices.searchPlaceholder')}
          />
          <SelectField
            label={t('invoices.client')}
            value={clientId}
            onChange={(value) => {
              setClientId(value);
              setPage(1);
            }}
            placeholder={t('common.all')}
            options={(clients.data?.data ?? []).map((client) => ({
              value: client.id,
              label: client.name ?? client.clientNumber,
            }))}
          />
          <SelectField
            label={t('invoices.paymentStatusLabel')}
            value={paymentStatus}
            onChange={(value) => {
              setPaymentStatus(value);
              setPage(1);
            }}
            placeholder={t('common.all')}
            options={ALL_PAYMENT_STATUSES.map((value) => ({
              value,
              label: t(`status.payment.${value}`),
            }))}
          />
        </FilterBar>

        <DataTable
          label={t('invoices.title')}
          columns={columns}
          rows={invoices.data?.data ?? []}
          rowKey={(invoice) => invoice.id}
          isLoading={invoices.isLoading}
          error={invoices.error}
          onRetry={() => void invoices.refetch()}
          emptyContent={search ? t('invoices.noneFound') : t('invoices.noneYet')}
          page={page}
          totalPages={invoices.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </Panel>
    </>
  );
}
